import { inRollout } from '../interceptor/geo-caller';
import { limiteDoPedido } from './order-payload-limits';

/**
 * Origem do pedido a partir da query string do link do cardápio — vai em `orders.external_source`.
 *
 * O cardápio antigo (AngularJS, `multipedidos-sistema/webview`) gravava a query string crua no
 * pedido: `pedir.delivery/loja?fbclid=…` virava `source = "fbclid=…"`. Integradoras usavam isso para
 * atribuir pedido a campanha da Meta/Google. Quando ele saiu do ar (07/07/2023) o pedir.delivery
 * passou a receber esse tráfego, mas só lia `?src=` — `fbclid`, `gclid` e `utm_*` caíam no chão.
 *
 * Regra:
 *  - `?src=` presente → vale ele, como sempre (o Wabot depende de `external_source == 'wabot'`).
 *  - senão → a query string, na ordem do link, SEM os parâmetros internos do app (token, mesa,
 *    carrinho…), cortada em `EXTERNAL_SOURCE_MAX` caracteres sem deixar `%XX` pela metade.
 *  - sem nada que sobre → null.
 *
 * Vai em `external_source`, não em `source`: `source` é o canal (`web`/`pos`/`ifood`…) e o backend
 * decide coisas por ele.
 *
 * INCIDENTE 11/10/2026 (MR !2072): a primeira versão cortava em 255 achando que a coluna era
 * `varchar(255)`. Ela é `varchar(191)` e a conexão do server é `strict` — 3.044 pedidos foram
 * RECUSADOS INTEIROS com `1406 Data too long for column 'external_source'` (link da bio do
 * Instagram: utm_source=ig&utm_medium=social&utm_content=link_in_bio&fbclid=…&utm_id=…, 194–295
 * caracteres). Por isso: coluna ampliada para varchar(1024) (server), corte seguro e dial por loja.
 */

/** Parâmetros que o próprio app usa — nunca são origem, e alguns são credenciais. */
export const PARAMETROS_INTERNOS: ReadonlySet<string> = new Set([
    'src', 'campaign',
    'u',                    // recuperação de carrinho
    'wpu',                  // token do telefone do WhatsApp
    't',                    // mesa
    'repeat-order-uuid',
    'token',                // redefinição de senha
    'pd_preview',           // prévia do layout novo
    // retorno do Mercado Pago / pagamento
    'preference_id', 'collection_id', 'collection_status', 'external_reference', 'payment_id',
    'payment_type', 'merchant_order_id', 'merchant_account_id', 'processing_mode', 'site_id',
    'status', 'cid', 'oid',
    'ot',                   // uuid do pedido na volta do pagamento (substitui cid+oid)
]);

/**
 * Tamanho da coluna `orders.external_source`, em CARACTERES, lido do contrato de limites do pedido
 * (`limiteDoPedido`, cópia de contracts/pedido-colunas.json do server). Nunca um número próprio: em
 * 11/10/2026 uma constante escrita à mão (255) passou da coluna real (191) e derrubou ~3.044 pedidos.
 * Hoje 1024 (`varchar(1024)`, migration server!9007 em produção desde 12/10 01:26 BRT).
 *
 * O MySQL conta CARACTERES (pontos de código), não bytes nem unidades UTF-16:
 *  - ramo da query: `URLSearchParams.toString()` só gera ASCII (o resto vira `%XX`, espaço vira `+`),
 *    então `.length` === caracteres === bytes — ver `cortarQuery`;
 *  - ramo do `?src=`: o valor vem DECODIFICADO (pode ter acento/emoji) e é cortado por ponto de
 *    código (`Array.from`), nunca por `.slice` — ver `cortarPorCaractere`.
 */
export const EXTERNAL_SOURCE_MAX = limiteDoPedido('details.externalSource');

/**
 * Corta por ponto de código. `'😀'.slice(0, 1)` deixa um substituto solto (`\ud83d`) que o
 * `JSON.stringify` manda como está e o `json_decode` do PHP recusa (JSON_ERROR_UTF16) — o corpo
 * inteiro do pedido viraria null. `Array.from` itera por ponto de código, que é o que o MySQL conta.
 */
export function cortarPorCaractere(valor: string, max: number = EXTERNAL_SOURCE_MAX): string {
    const pontos = Array.from(valor);
    return pontos.length <= max ? valor : pontos.slice(0, max).join('');
}

/**
 * Corta uma query JÁ codificada (só ASCII) em até `max` caracteres sem deixar escape pela metade:
 *  1. tira `%` ou `%X` incompleto do fim;
 *  2. tira a sequência UTF-8 incompleta do fim (`ç` = `%C3%A7`: cortar depois do `%C3` deixaria um
 *     byte líder sem continuação e o `decodeURIComponent` de quem ler lançaria);
 *  3. tira `&` solto do fim.
 */
export function cortarQuery(query: string, max: number = EXTERNAL_SOURCE_MAX): string {
    if (query.length <= max) return query;
    let s = query.slice(0, max).replace(/%[0-9A-Fa-f]?$/, '');

    const fim = s.match(/(?:%[0-9A-Fa-f]{2})+$/);
    if (fim) {
        const bytes = fim[0].match(/%[0-9A-Fa-f]{2}/g).map((h) => parseInt(h.slice(1), 16));
        let i = bytes.length - 1;
        while (i >= 0 && (bytes[i] & 0xc0) === 0x80) i--;            // volta pelos bytes de continuação
        const sobra = bytes.length - i;                              // bytes da última sequência
        if (i < 0) {
            s = s.slice(0, s.length - bytes.length * 3);             // só continuação: descarta
        } else {
            const b = bytes[i];
            const precisa = b >= 0xf0 ? 4 : b >= 0xe0 ? 3 : b >= 0xc0 ? 2 : 1;
            if (sobra < precisa) s = s.slice(0, s.length - sobra * 3);
        }
    }
    return s.replace(/&+$/, '');
}

/** O `?src=` como o master sempre mandou, só que nunca maior que a coluna. `null`/`''` passam iguais. */
export function srcDoLink(search: string | null | undefined): string | null {
    const src = new URLSearchParams(search || '').get('src');
    return src ? cortarPorCaractere(src) : src;
}

export function externalSourceFromSearch(search: string | null | undefined): string | null {
    const params = new URLSearchParams(search || '');

    const src = (params.get('src') || '').trim();
    if (src) return cortarPorCaractere(src);

    const resto = new URLSearchParams();
    params.forEach((valor, chave) => {
        if (!PARAMETROS_INTERNOS.has(chave)) resto.append(chave, valor);
    });

    return cortarQuery(resto.toString()) || null;
}

/**
 * Garante o limite num valor que JÁ estava guardado. A sessionStorage pode trazer um valor gravado
 * por outro bundle (ou editado à mão) — o envio nunca confia no que está guardado.
 */
export function limitarGuardado(valor: string | null): string | null {
    if (!valor) return null;
    if (Array.from(valor).length <= EXTERNAL_SOURCE_MAX) return valor;
    // Query guardada já está codificada (só ASCII); `src` guardado pode ter acento/emoji.
    return /^[\x20-\x7e]*$/.test(valor) ? cortarQuery(valor) : cortarPorCaractere(valor);
}

// v2: a chave do bundle de 11/10 (`externalSource.<loja>`) guarda valores cortados às cegas em 255.
const chave = (restaurantURL: string) => `externalSource.v2.${restaurantURL}`;

/**
 * Captura a origem do link de entrada e a mantém na aba.
 *
 * A query some da barra durante a navegação: o `reloadRestaurant` recarrega a loja pela URL limpa e
 * o reload de recuperação de chunk troca a barra pela URL canônica. Sem guardar, o pedido saía sem
 * origem mesmo o cliente tendo entrado pelo anúncio. Link novo COM origem substitui a guardada;
 * link sem origem não apaga.
 */
export function captureExternalSource(search: string, restaurantURL: string, storage: Storage | null): string | null {
    const daUrl = externalSourceFromSearch(search);

    try {
        if (daUrl) {
            storage?.setItem(chave(restaurantURL), daUrl);
            return daUrl;
        }
        return limitarGuardado(storage?.getItem(chave(restaurantURL)) || null);
    } catch (e) {
        return daUrl;
    }
}

/**
 * Dial da reentrega. Fora do dial o pedido sai como no master de antes da !2072: só o `?src=`.
 *
 * - `rolloutPercent`: `restaurant_id % 100 < N` (mesma conta do `inRollout` do geo-caller e do PDV;
 *   mesmos baldes — no degrau de 10% a coorte é a mesma do geo-caller).
 *   0 desliga (exige deploy). Entregue em 100 (decisão do Marlon, 12/10/2026): a guarda do server
 *   (server!9005) corta qualquer valor no tamanho real da coluna, então o pior caso deixou de ser
 *   pedido recusado e virou origem cortada.
 * - `lojasSempreDentro`: lojas de teste de uso livre (460 `teste460`, 10252 `testeqa`) — pela conta do
 *   dial elas caem em 60 e 52, e a validação em produção ficaria impossível com o dial baixo.
 * - `localStorage.pedirExternalSource`: `'off'` desliga no aparelho (suporte), `'on'` liga (QA).
 */
export const EXTERNAL_SOURCE_CONFIG: { rolloutPercent: number; lojasSempreDentro: number[] } = {
    rolloutPercent: 100,
    lojasSempreDentro: [460, 10252],
};

export const EXTERNAL_SOURCE_SWITCH_KEY = 'pedirExternalSource';

export function lerChaveExternalSource(): 'off' | 'on' | null {
    try {
        const v = String(window.localStorage.getItem(EXTERNAL_SOURCE_SWITCH_KEY) || '').trim().toLowerCase();
        return v === 'off' || v === 'on' ? v : null;
    } catch (e) {
        return null;
    }
}

export function capturaLigada(restaurantId: any, chaveDoAparelho: 'off' | 'on' | null = lerChaveExternalSource()): boolean {
    if (chaveDoAparelho === 'off') return false;
    if (chaveDoAparelho === 'on') return true;
    if (EXTERNAL_SOURCE_CONFIG.lojasSempreDentro.includes(Number(restaurantId))) return true;
    return inRollout(restaurantId, EXTERNAL_SOURCE_CONFIG.rolloutPercent);
}

/**
 * O que vai em `details.externalSource` no envio. Dentro do dial: a origem capturada (src ou query).
 * Fora: o `?src=` do link, como antes — sem sessionStorage, sem query.
 */
export function externalSourceParaPedido(
    detalhes: { src: string | null; capturada?: string | null } | null | undefined,
    restaurantId: any,
    chaveDoAparelho: 'off' | 'on' | null = lerChaveExternalSource(),
): string | null {
    if (!detalhes) return null;
    try {
        if (capturaLigada(restaurantId, chaveDoAparelho)) return detalhes.capturada ?? detalhes.src ?? null;
    } catch (e) {
        // falha para o lado do master: só o src
    }
    return detalhes.src;
}
